import React, { useEffect, useState, useRef } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";

/**
 * LiveCounter — banner brutalist con cifras animadas:
 *   - X descargas totales del libro
 *
 * Pide a /api/transparencia/stats (endpoint público read-only),
 * anima los números desde 0 y se queda mostrando un pulse.
 */
function useAnimatedNumber(target, durationMs = 1200) {
    const [value, setValue] = useState(0);
    const startRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        if (target == null) return;
        const from = 0;
        const to = Number(target) || 0;
        startRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        function tick(t) {
            if (startRef.current == null) startRef.current = t;
            const p = Math.min(1, (t - startRef.current) / durationMs);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(from + (to - from) * eased));
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
        }
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [target, durationMs]);

    return value;
}

export default function LiveCounter({ variant = "compact", className = "" }) {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/transparencia/stats")
            .then(({ data }) => {
                if (alive) setStats(data);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    const downloads = useAnimatedNumber(stats?.downloads_total ?? 0);

    if (variant === "compact") {
        return (
            <div
                className={`border border-dashed border-[var(--ink-dim)] bg-[#0d0d0d] px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 ${className}`}
                data-testid="live-counter-compact"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] flex items-center gap-2">
                    <span className="relative inline-flex w-2 h-2">
                        <span className="absolute inset-0 bg-[var(--warning)] animate-ping opacity-60" />
                        <span className="relative inline-block w-2 h-2 bg-[var(--warning)]" />
                    </span>
                    En vivo
                </span>
                <span className="font-mono text-xs text-[var(--paper-2)] inline-flex items-center gap-2">
                    <Download className="w-3.5 h-3.5 text-[var(--paper)]" />
                    <strong className="font-display text-lg tracking-tight">
                        {downloads.toLocaleString("es-CL")}
                    </strong>
                    <span className="text-[var(--ink-muted)]">personas ya lo descargaron</span>
                </span>
            </div>
        );
    }

    // variant === "full" — bloque grande
    return (
        <div
            className={`border-2 border-[var(--paper)] bg-[#0d0d0d] p-8 md:p-10 ${className}`}
            data-testid="live-counter-full"
        >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3 flex items-center gap-2">
                <span className="relative inline-flex w-2 h-2">
                    <span className="absolute inset-0 bg-[var(--danger)] animate-ping opacity-60" />
                    <span className="relative inline-block w-2 h-2 bg-[var(--danger)]" />
                </span>
                En vivo · datos reales
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--paper-2)] mb-10 leading-tight">
                Lo que llevamos hasta hoy
            </h2>
            <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-3 flex items-center gap-2">
                    <Download className="w-3.5 h-3.5 text-[var(--paper)]" />
                    Descargas totales
                </p>
                <p className="font-display text-6xl md:text-7xl lg:text-8xl leading-none text-[var(--paper-2)] tabular-nums">
                    {downloads.toLocaleString("es-CL")}
                </p>
            </div>
            <p className="font-mono text-[10px] text-[var(--ink-muted)] mt-8 leading-relaxed">
                Cifras actualizadas en tiempo real desde la base de datos del sitio
                + el periodo de beta privada. Sin datos personales de quienes descargaron.
            </p>
        </div>
    );
}
